import { FC, useEffect, useRef, useState } from "react";

import { Box, BoxProps, Stack, useTheme } from "@mui/material";
import ResizeObserver from "resize-observer-polyfill";

import { BaseFormV2Step1LayoutProps } from "./BaseFormV2Step1LayoutProps";

import { ThemedFormZeffyLogo } from "@/features/FormV2/sharedComponents/ThemedFormZeffyLogo";
import { useHasValueChanged } from "@/hooks/useHasValueChanged";
import { useMediaQuery } from "@/hooks/useMediaQuery";

const INFO_COLUMN_SCROLLING_OFFSET = "74px"; // Add an offset on the right column to align with the top of the summary block

const checkIfElementIsScrollingVertically = (element: Element) => {
  return element.scrollHeight > element.clientHeight;
};

const useIsStickyColumnScrolling = () => {
  const ref = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isScrolling, setIsScrolling] = useState(true);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (ref.current) {
        setIsScrolling(checkIfElementIsScrollingVertically(ref.current));
      }
    });

    // eslint-disable-next-line sonarjs/no-identical-functions
    const mutationObserver = new MutationObserver(() => {
      if (ref.current) {
        setIsScrolling(checkIfElementIsScrollingVertically(ref.current));
      }
    });

    if (ref.current && contentRef.current) {
      resizeObserver.observe(contentRef.current);
      mutationObserver.observe(ref.current, { childList: true, subtree: true });
    }

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  return {
    ref,
    contentRef,
    isScrolling,
  };
};

type BaseFormV2Step1TwoColumnLayoutProps = Pick<
  BaseFormV2Step1LayoutProps,
  "slots" | "contextDrawerHeight" | "showCheckoutFooter" | "logoHref" | "onLogoClick"
>;

export const BaseFormV2Step1TwoColumnLayout: FC<BaseFormV2Step1TwoColumnLayoutProps> = ({
  slots,
  contextDrawerHeight,
  showCheckoutFooter = false,
  logoHref,
  onLogoClick,
}) => {
  const theme = useTheme();
  const { isSmallScreen } = useMediaQuery();

  const hasBanner = Boolean(slots.banner);
  const [animateCheckoutFooter, watchedShowCheckoutFooter] = useHasValueChanged(showCheckoutFooter);

  const {
    ref: infoColumnRef,
    contentRef: infoColumnContentRef,
    isScrolling: isInfoColumnScrolling,
  } = useIsStickyColumnScrolling();
  const {
    ref: orderColumnRef,
    contentRef: orderColumnContentRef,
    isScrolling: isOrderColumnScrolling,
  } = useIsStickyColumnScrolling();

  const renderStickyColumn = (
    ref: React.RefObject<HTMLDivElement | null>,
    contentRef: React.RefObject<HTMLDivElement | null>,
    content: React.ReactNode,
    sx: {
      width: BoxProps["width"];
      paddingInlineStart: BoxProps["paddingInlineStart"];
      paddingInlineEnd: BoxProps["paddingInlineEnd"];
    },
    isScrolling: boolean
  ) => {
    return (
      <Box
        ref={ref}
        display={{ md: "flex" }}
        height={{ md: `calc(100dvh - ${contextDrawerHeight}px)` }}
        sx={{
          width: sx.width,
          alignItems: { md: isScrolling ? "flex-start" : "center" },
          boxSizing: "border-box",
          overflow: { md: "auto" },
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          msOverflowStyle: "none", // for Internet Explorer and Edge
          zIndex: 2,
        }}
      >
        <Box
          width="100%"
          sx={{
            paddingBlock: { md: 6, lg: 8 },
            paddingInlineStart: sx.paddingInlineStart,
            paddingInlineEnd: sx.paddingInlineEnd,
          }}
        >
          <Box ref={contentRef} width="100%" sx={{ position: "relative", zIndex: 2 }}>
            {content}
          </Box>
        </Box>
      </Box>
    );
  };

  const renderAnimatedCheckoutFooter = () => {
    const hidden = !watchedShowCheckoutFooter && !animateCheckoutFooter;

    return (
      <Box
        data-test="form-v2-checkout-footer"
        position={{ xs: "fixed", md: "sticky" }}
        // The sticky footer should always stick to 40px from the bottom of the screen
        bottom={{ xs: 0, md: theme.spacing(5) }}
        left={0}
        right={0}
        marginBlockStart={{ md: hidden ? 0 : 2 }}
        padding={{ xs: hidden ? 0 : 2, md: 0 }}
        sx={{
          animation: animateCheckoutFooter
            ? watchedShowCheckoutFooter
              ? "fade-slide-in 0.3s ease-in-out both"
              : "fade-slide-out 0.3s ease-in-out both"
            : undefined,
          opacity: hidden ? 0 : undefined,
          height: hidden ? 0 : undefined,
          zIndex: 1000,
          ["@keyframes fade-slide-in"]: {
            from: { translate: "0 20px", opacity: 0 },
            to: { translate: "0 0", opacity: 1 },
          },
          ["@keyframes fade-slide-out"]: {
            from: { translate: "0 0", opacity: 1 },
            to: { translate: "0 20px", opacity: 0 },
          },
        }}
      >
        {slots.checkoutFooter}
      </Box>
    );
  };

  return (
    <Stack gap={{ xs: 0, md: 4 }}>
      <Stack gap={2} width="100%">
        {isSmallScreen && slots.banner}
        <Stack
          direction={{ md: "row" }}
          gap={{
            xs: 2.5,
            md: 0,
          }}
          width="100%"
          padding={{ xs: theme.spacing(hasBanner ? 0 : 4, 2, 0, 2), md: 0 }}
        >
          {renderStickyColumn(
            infoColumnRef,
            infoColumnContentRef,
            slots.info,
            {
              width: { md: "50%", lg: "47%" },
              paddingInlineStart: { md: 4, lg: 10, xl: "calc((100vw - 1375px) / 2)" },
              paddingInlineEnd: { md: 1.5, lg: 3, xl: 0 },
            },
            isInfoColumnScrolling
          )}
          {isSmallScreen && slots.divider}
          {renderStickyColumn(
            orderColumnRef,
            orderColumnContentRef,
            <Stack
              width="100%"
              paddingBlockStart={{ md: isInfoColumnScrolling ? INFO_COLUMN_SCROLLING_OFFSET : undefined }}
            >
              {slots.order}
              {!isSmallScreen && renderAnimatedCheckoutFooter()}
            </Stack>,
            {
              width: { md: "50%", lg: "53%" },
              paddingInlineStart: { md: 1.5, lg: 3 },
              paddingInlineEnd: { md: 4, lg: 10, xl: "calc((100vw - 1375px) / 2)" },
            },
            isOrderColumnScrolling
          )}
        </Stack>
      </Stack>
      <Box
        sx={{
          position: { md: "fixed" },
          bottom: { md: 24 },
          right: { md: 24 },
          display: { xs: "flex", md: "block" },
          justifyContent: { xs: "center", md: "unset" },
          paddingBlock: { xs: 2, md: 0 },
          zIndex: 1,
        }}
      >
        <ThemedFormZeffyLogo href={logoHref} onClick={onLogoClick} />
      </Box>
      {isSmallScreen && (
        <>
          {/* Keep space in the page for the sticky checkout button */}
          {watchedShowCheckoutFooter && <Box sx={{ height: 75, visibility: "hidden" }} />}
          {renderAnimatedCheckoutFooter()}
        </>
      )}
    </Stack>
  );
};
